import { WALL_TILE, type WallLength, wallTiles, wallViewBox } from '@league/shared/marks';

/**
 * The Wall — four walls × nineteen stacks × two tiles high = 152.
 *
 * Every game begins by building the complete set, so the pattern is a COUNT of the number rather
 * than a picture of it. That is the whole idea, and it is why the tile geometry is fixed rather
 * than a decorative flourish somebody can restyle.
 *
 * THE GOVERNING RULE: DECORATION LIVES WHERE THERE IS NO DATA. A pattern behind a standings table
 * is noise; the same pattern on a sign-in screen is identity. UI-UX-SPEC §2 lists where it may
 * appear, and the list is not a suggestion:
 *
 *   Yes — public site section heads, sign-in, print pack borders, email bands, public standings
 *   No  — every director and commissioner working screen
 *   No  — the seating board, ever. Tiles behind draggable seat cards is actively confusing.
 *
 * There is no prop that turns those rules off, because a prop is how an exception becomes the norm.
 */
export interface WallProps {
  /** The whole set, or one wall of it. */
  length: WallLength;
  /**
   * What is BEHIND the wall.
   *
   * ⚠ `sand` WAS ADDED 2026-09-12 BECAUSE `light` IS SAND. The light-ground tile fill is
   * `--brand-sand`, which is a ~1.2:1 texture on chalk, paper and `--light` — and exactly 1:1 on a
   * sand band, where the wall disappears completely. The public standings page had a `light` wall
   * on `bg-sand` and rendered as a single red tile floating in an empty band, with nothing behind
   * it: the drawn tile was the only part of the wall anybody could see.
   *
   * The palette has four light grounds and this only knew two, so "light" was quietly standing in
   * for "not navy". Naming sand separately is what makes the invisible case unrepresentable.
   */
  ground: 'light' | 'sand' | 'navy';
  /**
   * Which stack has its top tile lifted out. Omit for a plain wall.
   *
   * ONE TILE LIFTED MEANS A TILE IN PLAY — use it only where something is genuinely in progress,
   * never as static ornament. Its POSITION is the variable; there is deliberately no way to give it
   * a different colour or to draw a second one.
   */
  drawnStack?: number;
  className?: string;
}

/**
 * On navy the tiles are `--deep-lift`, one step up from the ground — the only value that reads on
 * navy without being text. The drawn tile is `--signal` on both grounds.
 *
 * The tiles sit at ~1.2:1 against their ground on purpose. This is a texture, not information: at
 * anything stronger it competes with the words in front of it, which is the failure the placement
 * rules above exist to prevent.
 */
const TILE: Record<WallProps['ground'], string> = {
  light: 'var(--brand-sand)',
  /*
    `--stroke`, going DARKER rather than lighter. On sand the only other candidate is `--light`, one
    step up, which measures about 1.08:1 — still effectively invisible. `--stroke` is the hairline
    token, which is to say it is already the palette's value for "a texture you can just see", and
    it lands at roughly the same 1.2:1 the other two grounds get.
  */
  sand: 'var(--brand-stroke)',
  navy: 'var(--brand-deep-lift)',
};

/**
 * Internal, and its optionals are explicit `| undefined` rather than `?` — the repo runs
 * `exactOptionalPropertyTypes`, so a computed `number | undefined` cannot be passed to a `?` prop.
 */
function OneWall({
  length,
  ground,
  drawnStack,
  className,
}: {
  length: WallLength;
  ground: WallProps['ground'];
  drawnStack: number | undefined;
  className: string | undefined;
}) {
  return (
    <svg
      viewBox={wallViewBox(length)}
      preserveAspectRatio="xMidYMid meet"
      className={className}
      /** A texture. It says nothing a screen reader needs, and announcing it on every section head
       *  would be maddening. */
      aria-hidden={true}
      focusable="false"
    >
      {wallTiles(length, drawnStack).map((tile, i) => (
        <rect
          key={i}
          x={tile.x}
          y={tile.y}
          width={WALL_TILE.width}
          height={WALL_TILE.height}
          rx={WALL_TILE.radius}
          fill={tile.drawn ? 'var(--brand-signal)' : TILE[ground]}
        />
      ))}
    </svg>
  );
}

export function Wall({ length, ground, drawnStack, className }: WallProps) {
  /**
   * BELOW 700px THE FULL WALL BECOMES ONE WALL, and it is done with a media query rather than by
   * measuring the window in JavaScript.
   *
   * The 152-tile wall compresses to an unreadable grey stripe on a phone — 76 stacks across 390px
   * is under 5px a stack. Measuring in an effect would mean the server renders one thing and the
   * browser swaps it after hydration, which is a visible flash on the marketing pages this appears
   * on most. Two elements and a breakpoint costs a few dozen rects and never flashes.
   *
   * 700px is not a Tailwind breakpoint. It is the brand board's number, so it is written as one.
   */
  if (length === 19) {
    return <OneWall length={19} ground={ground} drawnStack={drawnStack} className={className} />;
  }

  /**
   * The drawn tile keeps its PLACE ALONG THE WALL, not its stack number. Stack 30 of 76 is
   * two-fifths of the way along; on the 19-stack wall that is stack 8. Passing 30 straight through
   * would put it off the end, and the narrow version would silently lose the one tile that means
   * something.
   */
  const narrowDrawn =
    drawnStack === undefined ? undefined : Math.round((drawnStack / 76) * 19);

  return (
    <>
      <OneWall
        length={19}
        ground={ground}
        drawnStack={narrowDrawn}
        className={`min-[700px]:hidden ${className ?? ''}`}
      />
      <OneWall
        length={152}
        ground={ground}
        drawnStack={drawnStack}
        className={`max-[699px]:hidden ${className ?? ''}`}
      />
    </>
  );
}
